/**
 * The switcher's menu: your projects by name (the current one checked) and
 * "New project". It floats (`ui/floating.tsx`), so the sidebar's overflow
 * never clips it. Keyboard as the
 * WAI-ARIA menu pattern: focus moves in, ↑ ↓ Home End move, Escape closes and
 * returns focus to the button. More than 8 projects get a filter box. Each
 * project's ⚙ opens its settings, a mouse shortcut like Langfuse's (the arrow
 * keys skip it; Settings is in the sidebar too).
 */

import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { Check, Plus, Settings } from "lucide-react";
import { Floating } from "@/components/ui/floating";
import { openProject } from "@/lib/project-url";
import type { Project } from "./types";

const FILTER_FROM = 8;

const itemClass =
  "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-body outline-none hover:bg-nav-hover hover:text-ink focus:bg-nav-hover focus:text-ink";

const gearClass =
  "absolute top-1/2 right-1 flex size-6 -translate-y-1/2 items-center justify-center rounded text-faint opacity-0 hover:bg-nav-active hover:text-ink group-hover:opacity-100";

/** By name: the switcher is a plain list. */
const ordered = (projects: Project[]) => [...projects].sort((a, b) => a.name.localeCompare(b.name));

export function ProjectMenu({
  anchor,
  current,
  projects,
  onClose,
  onCreate,
}: {
  anchor: RefObject<HTMLElement | null>;
  current: string;
  projects: { data?: Project[] | undefined; isPending: boolean; isError: boolean };
  onClose: (refocus?: boolean) => void;
  onCreate: () => void;
}) {
  const menu = useRef<HTMLDivElement>(null);
  const filterBox = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const all = projects.data ? ordered(projects.data) : [];
  const filterable = all.length > FILTER_FROM;
  const q = query.trim().toLowerCase();
  const shown = q ? all.filter((p) => p.name.toLowerCase().includes(q)) : all;

  const items = () => [...(menu.current?.querySelectorAll<HTMLElement>("[role^=menuitem]") ?? [])];

  // Focus moves in once the list is there: the filter box, else the current project.
  const loaded = projects.data !== undefined;
  useEffect(() => {
    if (filterBox.current) return filterBox.current.focus();
    const list = items();
    (list.find((el) => el.getAttribute("aria-checked") === "true") ?? list[0])?.focus();
  }, [loaded]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") return (e.preventDefault(), e.stopPropagation(), onClose());
    if (e.key === "Tab") return onClose(false);
    const list = items();
    const at = list.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => (e.preventDefault(), list[(i + list.length) % list.length]?.focus());
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at <= 0 ? list.length - 1 : at - 1);
    else if (e.key === "Home" && at >= 0) go(0);
    else if (e.key === "End" && at >= 0) go(list.length - 1);
  };

  const pick = (slug: string) => (slug === current ? onClose() : openProject(slug));

  return (
    <Floating
      ref={menu}
      anchor={anchor}
      onDismiss={() => onClose(false)}
      role="menu"
      aria-label="Projects"
      onKeyDown={onKeyDown}
      minWidth={220}
      className="flex flex-col overflow-y-auto p-1"
    >
      {filterable && (
        <div className="p-1 pb-1.5">
          <input
            ref={filterBox}
            aria-label="Find a project"
            placeholder="Find a project…"
            className="inp h-8 w-full text-[13px]"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      {projects.isPending && <p className="px-2 py-1.5 text-[13px] text-muted">Loading…</p>}
      {projects.isError && <p className="px-2 py-1.5 text-[13px] text-err">Could not load your projects.</p>}
      {loaded && shown.length === 0 && <p className="px-2 py-1.5 text-[13px] text-muted">No matches</p>}
      {shown.map((p) => (
        <div key={p.id} className="group relative">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={p.slug === current}
            tabIndex={-1}
            title={p.name}
            className={`${itemClass} pr-8`}
            onClick={() => pick(p.slug)}
          >
            <span className="flex w-3.5 shrink-0 justify-center">
              {p.slug === current && <Check size={13} strokeWidth={2.5} aria-hidden />}
            </span>
            <span className="min-w-0 flex-1 truncate">{p.name}</span>
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label={`Settings for ${p.name}`}
            title="Project settings"
            className={gearClass}
            onClick={() => openProject(p.slug, { path: "/settings" })}
          >
            <Settings size={13} aria-hidden />
          </button>
        </div>
      ))}
      <div role="separator" className="my-1 border-t border-line-soft" />
      <button type="button" role="menuitem" tabIndex={-1} className={itemClass} onClick={onCreate}>
        <Plus size={14} className="w-3.5 shrink-0" aria-hidden />
        New project
      </button>
    </Floating>
  );
}
